/**
 * Memorial Video Players for Landing Page
 * - ALWAYS starts muted with large sound overlay button
 * - First click on sound button restarts from 0:00 with audio
 * - After first enable, button shrinks to normal mute toggle
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Video, RefreshCw, Volume2, VolumeX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook to fetch fresh playback URL for a slot
 */
const usePlaybackUrl = (slot) => {
  const [url, setUrl] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);
  const [hasVideo, setHasVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  const fetchUrl = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch(`${API}/memorial-videos/playback/${slot}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch playback URL: ${res.status}`);
      }
      
      const data = await res.json();
      
      setHasVideo(data.hasVideo);
      setUrl(data.url);
      setPosterUrl(data.posterUrl);
      setExpiresAt(data.expiresAt);
      
    } catch (err) {
      console.error(`[MEMORIAL-PLAYER] Failed to fetch ${slot} playback URL:`, err);
      setError(err.message);
      setHasVideo(false);
    } finally {
      setLoading(false);
    }
  }, [slot]);

  // Initial fetch
  useEffect(() => {
    fetchUrl();
  }, [fetchUrl]);

  // Auto-refresh URL before expiration
  useEffect(() => {
    if (!expiresAt || !hasVideo) return;
    
    const expiryTime = new Date(expiresAt).getTime();
    const refreshTime = expiryTime - (2 * 60 * 1000); // Refresh 2 minutes before expiry
    const now = Date.now();
    const delay = Math.max(refreshTime - now, 0);
    
    if (delay > 0) {
      const timer = setTimeout(() => {
        console.log(`[MEMORIAL-PLAYER] Auto-refreshing ${slot} URL before expiration`);
        fetchUrl();
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [expiresAt, hasVideo, slot, fetchUrl]);

  return { url, posterUrl, hasVideo, loading, error, retry: fetchUrl };
};


/**
 * Hero Video Player for Memorial page
 * - ALWAYS starts muted with large sound overlay button
 * - First click on sound button restarts from 0:00 with audio
 * - After first enable, button shrinks to normal mute toggle
 */
export const MemorialHeroVideoPlayer = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [videoError, setVideoError] = useState(false);
  // Per-player flag: has audio been enabled at least once?
  const hasStartedWithSoundRef = useRef(false);
  // Track previous URL to detect src changes
  const prevUrlRef = useRef(null);
  
  // Fetch metadata (hasVideo, poster) but use stable streaming URL
  const { posterUrl, hasVideo, loading, error } = usePlaybackUrl('hero');
  const stableUrl = hasVideo ? `${API}/storage/memorial-video/hero` : null;

  /**
   * Force muted state on mount and whenever src changes
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Detect src change - reset the sound flag
    if (prevUrlRef.current !== stableUrl && stableUrl) {
      console.log('[MEMORIAL-HERO] URL changed - resetting sound state');
      hasStartedWithSoundRef.current = false;
      setIsMuted(true);
    }
    prevUrlRef.current = stableUrl;
    
    // ALWAYS ensure video starts muted
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
  }, [stableUrl]);

  // Autoplay when video loads (always muted)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stableUrl || videoError) return;

    // Ensure muted before autoplay
    video.muted = true;
    video.play().catch((err) => {
      console.log('[MEMORIAL-HERO] Autoplay blocked:', err.message);
    });
  }, [stableUrl, videoError]);

  /**
   * First-interaction audio restart logic - ONLY triggered by overlay button
   */
  const handleSoundButtonClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    if (!hasStartedWithSoundRef.current) {
      // First time enabling sound - restart from beginning
      console.log('[MEMORIAL-HERO] First sound enable - restarting from 0:00');
      video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      video.play().catch((err) => {
        console.log('[MEMORIAL-HERO] Play after unmute blocked:', err.message);
      });
      hasStartedWithSoundRef.current = true;
      setIsMuted(false);
    } else {
      // Already started with sound before - just toggle mute normally (no restart)
      video.muted = !video.muted;
      video.volume = video.muted ? 0 : 1;
      setIsMuted(video.muted);
    }
  }, []);

  const handleVideoError = () => {
    console.error('[MEMORIAL-HERO] Video playback error');
    setVideoError(true);
  };

  // Loading state - render nothing (prevents flash/glitch)
  if (loading) {
    return null;
  }

  // No video configured - render nothing (no placeholder)
  if (!hasVideo && !loading) {
    return null;
  }

  // Error state - render nothing (clean page)
  if ((error || videoError) && !loading) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Glow Effect */}
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-purple-500/20 via-purple-400/30 to-purple-500/20 rounded-3xl blur-2xl opacity-60"></div>
      
      {/* Video Container */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20 ring-1 ring-purple-500/20 bg-black group">
        <video
          ref={videoRef}
          src={stableUrl}
          poster={posterUrl}
          className="w-full aspect-video object-cover [&::-webkit-media-controls]:opacity-0 md:[&::-webkit-media-controls]:group-hover:opacity-100 [&::-webkit-media-controls]:transition-opacity"
          muted
          loop
          playsInline
          controls
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          preload="metadata"
          onError={handleVideoError}
          data-testid="memorial-hero-video"
        />
        
        {/* Sound Overlay Button - LARGE when muted (first time), small after first enable */}
        <button
          onClick={handleSoundButtonClick}
          className={`absolute z-10 transition-all duration-300 ${
            isMuted && !hasStartedWithSoundRef.current
              ? 'top-4 right-4 px-4 py-3 bg-black/70 hover:bg-black/90 rounded-xl backdrop-blur-sm flex items-center gap-2 border border-white/20'
              : 'top-4 right-4 p-3 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm'
          }`}
          title={isMuted ? "Tap for sound" : "Mute"}
          data-testid="memorial-hero-sound-toggle"
        >
          {isMuted ? (
            <>
              <VolumeX className={`${!hasStartedWithSoundRef.current ? 'w-6 h-6' : 'w-5 h-5'} text-white`} />
              {!hasStartedWithSoundRef.current && (
                <span className="text-white font-medium text-sm">Tap for sound</span>
              )}
            </>
          ) : (
            <Volume2 className="w-5 h-5 text-purple-400" />
          )}
        </button>
      </div>
    </div>
  );
};


/**
 * Demo Video Player for Memorial page
 * - ALWAYS starts muted with large sound overlay button
 * - First click on sound button restarts from 0:00 with audio
 * - After first enable, button shrinks to normal mute toggle
 */
export const MemorialDemoVideoPlayer = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [videoError, setVideoError] = useState(false);
  // Per-player flag: has audio been enabled at least once?
  const hasStartedWithSoundRef = useRef(false);
  // Track previous URL to detect src changes
  const prevUrlRef = useRef(null);
  
  // Use stable same-origin streaming URL (no presigned expiry, no CORS)
  const stableUrl = `${API}/storage/memorial-video/demo`;
  const { posterUrl, hasVideo, loading, error } = usePlaybackUrl('demo');

  /**
   * Force muted state on mount and whenever src changes
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    // Detect src change - reset the sound flag
    if (prevUrlRef.current !== stableUrl && stableUrl) {
      console.log('[MEMORIAL-DEMO] URL changed - resetting sound state');
      hasStartedWithSoundRef.current = false;
      setIsMuted(true);
    }
    prevUrlRef.current = stableUrl;
    
    // ALWAYS ensure video starts muted
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
  }, [stableUrl]);

  // Intersection Observer for viewport detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Play/Pause based on visibility (always muted autoplay)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || videoError || !stableUrl) return;

    if (isInView) {
      // Belt-and-suspenders: ensure DOM muted attribute before play
      video.muted = true;
      video.defaultMuted = true;
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    } else if (!isMuted) {
      // Only pause if sound is off (user might be listening)
    } else {
      video.pause();
    }
  }, [isInView, videoError, stableUrl, isMuted]);

  // Handle video errors
  const handleVideoError = () => {
    console.error('[MEMORIAL-DEMO] Video playback error');
    setVideoError(true);
  };

  /**
   * First-interaction audio restart logic - ONLY triggered by overlay button
   */
  const handleSoundButtonClick = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    
    if (!hasStartedWithSoundRef.current) {
      // First time enabling sound - restart from beginning
      console.log('[MEMORIAL-DEMO] First sound enable - restarting from 0:00');
      video.currentTime = 0;
      video.muted = false;
      video.volume = 1;
      video.play().catch((err) => {
        console.log('[MEMORIAL-DEMO] Play after unmute blocked:', err.message);
      });
      hasStartedWithSoundRef.current = true;
      setIsMuted(false);
    } else {
      // Already started with sound before - just toggle mute normally (no restart)
      video.muted = !video.muted;
      video.volume = video.muted ? 0 : 1;
      setIsMuted(video.muted);
    }
  }, []);

  // Loading state
  if (loading) {
    return null;
  }

  if (!hasVideo && !loading) {
    return null;
  }

  if ((error || videoError) && !loading) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Glow Effect */}
      <div className="absolute -inset-4 md:-inset-6 bg-gradient-to-r from-purple-500/20 via-purple-400/30 to-purple-500/20 rounded-3xl blur-2xl opacity-60"></div>
      
      {/* Video Container */}
      <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/20 ring-1 ring-purple-500/20 bg-black group">
        <video
          ref={videoRef}
          src={stableUrl}
          poster={posterUrl}
          className="w-full aspect-video object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onError={handleVideoError}
          data-testid="memorial-demo-video"
        />
        
        {/* Sound Overlay Button - LARGE when muted (first time), small after first enable */}
        <button
          onClick={handleSoundButtonClick}
          className={`absolute z-10 transition-all duration-300 ${
            isMuted && !hasStartedWithSoundRef.current
              ? 'top-4 right-4 px-4 py-3 bg-black/70 hover:bg-black/90 rounded-xl backdrop-blur-sm flex items-center gap-2 border border-white/20'
              : 'top-4 right-4 p-3 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm'
          }`}
          title={isMuted ? "Tap for sound" : "Mute"}
          data-testid="memorial-demo-sound-toggle"
        >
          {isMuted ? (
            <>
              <VolumeX className={`${!hasStartedWithSoundRef.current ? 'w-6 h-6' : 'w-5 h-5'} text-white`} />
              {!hasStartedWithSoundRef.current && (
                <span className="text-white font-medium text-sm">Tap for sound</span>
              )}
            </>
          ) : (
            <Volume2 className="w-5 h-5 text-purple-400" />
          )}
        </button>
      </div>
    </div>
  );
};

export default { MemorialHeroVideoPlayer, MemorialDemoVideoPlayer };
